import { message, Skeleton } from "antd";
import { useCallback, useEffect, useState } from "react";
import AuthLayout from "layout/AuthLayout";
import { getLoanProfile } from "services/LoanService";
import LoanProfileEditModal from "./LoanProfileEditModal";

export function LoanProfilePage() {
  const [loading, setLoading] = useState(true);
  const [loanProfile, setLoanProfile] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editSection, setEditSection] = useState(null);

  const fetchLoanProfile = useCallback(async () => {
    setLoading(true);

    try {
      const res = await getLoanProfile();

      const _loanProfile = res?.data?.data;

      if (_loanProfile) {
        setLoanProfile(_loanProfile);
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to retrieve loan profile",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLoanProfile();
  }, [fetchLoanProfile]);

  function openEdit(section) {
    setEditSection(section);
    setEditOpen(true);
  }

  function closeEdit() {
    setEditOpen(false);
    setEditSection(null);
  }

  function handleUpdated(profile) {
    setLoanProfile(profile);
    closeEdit();
  }

  if (loading) {
    return (
      <AuthLayout>
        <LoanProfileLoader />
      </AuthLayout>
    );
  }

  if (!loanProfile) {
    return (
      <AuthLayout>
        <div className="p-4 sm:p-6 max-w-5xl mx-auto">
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
            <div className="text-4xl text-gray-400 mb-4">
              <i className="bi bi-file-earmark-person"></i>
            </div>

            <h2 className="font-poppins text-lg font-semibold text-primary">
              Loan Profile Not Found
            </h2>

            <p className="font-poppins text-sm text-gray-500 mt-2">
              Complete a loan application first to create your reusable loan
              profile.
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="p-4 sm:p-6 max-w-5xl mx-auto">
        <header className="mb-6">
          <h1 className="font-poppins text-xl sm:text-2xl font-semibold text-primary">
            Loan Profile
          </h1>

          <p className="font-poppins text-sm text-gray-500 mt-1">
            Manage the information that will be reused when you apply for
            another loan.
          </p>
        </header>

        <div className="space-y-5">
          {/* <ProfileSection
            title="Bank Account"
            icon="bi-bank"
            onEdit={() => openEdit("bank_account")}
          >
            <InfoGrid>
              <InfoItem
                label="Account Name"
                value={loanProfile.bank_account?.account_name}
              />

              <InfoItem
                label="Account Number"
                value={loanProfile.bank_account?.account_number}
              />

              <InfoItem
                label="Bank Name"
                value={loanProfile.bank_account?.bank_name}
              />

              <InfoItem
                label="Bank Code"
                value={loanProfile.bank_account?.bank_code}
              />
            </InfoGrid>
          </ProfileSection> */}

          {/* <ProfileSection
            title="Verification & Employment"
            icon="bi-person-vcard"
            onEdit={() => openEdit("verification")}
          >
            <InfoGrid>
              <InfoItem label="BVN" value={loanProfile.bvn} />

              <InfoItem
                label="Employment Status"
                value={loanProfile.employment_status}
              />

              <InfoItem label="Certificate" value={loanProfile.certificate} />

              <InfoItem
                label="Other Certificate"
                value={loanProfile.othercertificate}
              />
            </InfoGrid>
          </ProfileSection> */}

          <ProfileSection
            title="Institution"
            icon="bi-building"
            onEdit={() => openEdit("institution")}
          >
            <InfoGrid>
              <InfoItem
                label="Institution"
                value={loanProfile.institution?.name}
              />

              <InfoItem
                label="Course"
                value={loanProfile.institution?.course}
              />

              <InfoItem
                label="Faculty"
                value={loanProfile.institution?.faculty}
              />

              <InfoItem
                label="Matric Number"
                value={loanProfile.institution?.matric_number}
              />

              <InfoItem
                label="Current Study Year"
                value={loanProfile.institution?.current_study_year}
              />

              <InfoItem
                label="Graduation Year"
                value={loanProfile.institution?.graduation_year}
              />
            </InfoGrid>
          </ProfileSection>

          <ProfileSection
            title="Guarantor One"
            icon="bi-person-check"
            onEdit={() => openEdit("guarantor_one")}
          >
            <GuarantorDetails guarantor={loanProfile.guarantor_one} />
          </ProfileSection>

          <ProfileSection
            title="Guarantor Two"
            icon="bi-person-check"
            onEdit={() => openEdit("guarantor_two")}
          >
            <GuarantorDetails guarantor={loanProfile.guarantor_two} />
          </ProfileSection>

          <ProfileSection
            title="Documents"
            icon="bi-folder2-open"
            onEdit={() => openEdit("documents")}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DocumentItem
                label="Admission Letter"
                value={loanProfile.admission_letter}
              />

              <DocumentItem
                label="School Fee Receipt"
                value={loanProfile.school_fee_receipt}
              />
            </div>
          </ProfileSection>
        </div>
      </div>

      <LoanProfileEditModal
        open={editOpen}
        section={editSection}
        profile={loanProfile}
        onClose={closeEdit}
        onUpdated={handleUpdated}
      />
    </AuthLayout>
  );
}

function ProfileSection({ title, icon, children, onEdit }) {
  return (
    <section className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <header className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-primary-50 text-primary-700 flex items-center justify-center">
            <i className={`bi ${icon}`}></i>
          </div>

          <h2 className="font-poppins text-sm sm:text-base font-semibold text-primary">
            {title}
          </h2>
        </div>

        <button
          type="button"
          onClick={onEdit}
          className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-primary-700 hover:text-primary-900"
        >
          <i className="bi bi-pencil"></i>
          <span>Edit</span>
        </button>
      </header>

      <div className="p-4 sm:p-6">{children}</div>
    </section>
  );
}

function InfoGrid({ children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
      {children}
    </div>
  );
}

function InfoItem({ label, value }) {
  return (
    <div>
      <p className="font-poppins text-xs text-gray-500 mb-1">{label}</p>

      <p className="font-poppins text-sm text-gray-800 font-medium break-words">
        {value || "Not provided"}
      </p>
    </div>
  );
}

function GuarantorDetails({ guarantor }) {
  return (
    <InfoGrid>
      <InfoItem label="First Name" value={guarantor?.first_name} />
      <InfoItem label="Middle Name" value={guarantor?.middle_name} />
      <InfoItem label="Surname" value={guarantor?.surname} />
      <InfoItem label="Phone" value={guarantor?.phone} />
      <InfoItem label="Email" value={guarantor?.email} />
      <InfoItem label="Work Place" value={guarantor?.work_place} />
      <InfoItem label="NIN" value={guarantor?.nin} />
    </InfoGrid>
  );
}

function DocumentItem({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 border border-gray-200 rounded-lg p-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="h-9 w-9 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
          <i className="bi bi-file-earmark text-primary"></i>
        </div>

        <div className="min-w-0">
          <p className="font-poppins text-xs text-gray-500">{label}</p>

          <p className="font-poppins text-sm font-medium text-primary truncate">
            {value ? "Uploaded" : "Not provided"}
          </p>
        </div>
      </div>

      {value && (
        <a
          href={value}
          target="_blank"
          rel="noreferrer"
          className="text-xs font-medium text-primary-700 hover:underline"
        >
          View
        </a>
      )}
    </div>
  );
}

function LoanProfileLoader() {
  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <Skeleton.Input
        active
        style={{
          width: 180,
          height: 30,
          marginBottom: 8,
        }}
      />

      <div className="mb-6">
        <Skeleton.Input
          active
          style={{
            width: 400,
            height: 18,
          }}
        />
      </div>

      <div className="space-y-5">
        {new Array(6).fill("").map((_, index) => (
          <div
            key={`loan-profile-loader-${index}`}
            className="bg-white border border-gray-200 rounded-xl p-5"
          >
            <Skeleton active paragraph={{ rows: 2 }} />
          </div>
        ))}
      </div>
    </div>
  );
}
